import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useLanguage } from '@contexts/language.context'
import { siteMetadata } from '../../config/metadata'

// Website schema.org data
interface WebsiteSchemaProps {
  siteUrl?: string
  siteName?: string
}

export const WebsiteSchema: React.FC<WebsiteSchemaProps> = ({
  siteUrl = siteMetadata.siteUrl,
  siteName = siteMetadata.siteName,
}) => {
  const { currentLanguage } = useLanguage()
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: siteUrl,
    name: siteName,
    inLanguage: currentLanguage,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  }
  
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

// Organization schema.org data
interface OrganizationSchemaProps {
  name?: string
  logo?: string
  url?: string
  socialUrls?: string[]
  description?: string
}

export const OrganizationSchema: React.FC<OrganizationSchemaProps> = ({
  name = siteMetadata.siteName,
  logo = siteMetadata.organizationLogo,
  url = siteMetadata.siteUrl,
  socialUrls = [],
  description,
}) => {
  const { currentLanguage } = useLanguage()
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name,
    url,
    logo: logo.startsWith('http') ? logo : `${url}${logo}`,
    inLanguage: currentLanguage,
    sameAs: socialUrls,
    ...(description && { description }),
  }
  
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

// Breadcrumbs schema.org data
interface BreadcrumbItem {
  name: string
  url: string
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[]
}

export const BreadcrumbSchema: React.FC<BreadcrumbSchemaProps> = ({ items }) => {
  const { currentLanguage } = useLanguage()
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    inLanguage: currentLanguage,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }
  
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

// Article schema.org data
interface ArticleSchemaProps {
  title: string
  description: string
  url: string
  imageUrl: string
  datePublished: string
  dateModified?: string
  authorName: string
  authorUrl?: string
  publisherName?: string
  publisherLogo?: string
}

export const ArticleSchema: React.FC<ArticleSchemaProps> = ({
  title,
  description,
  url,
  imageUrl,
  datePublished,
  dateModified,
  authorName,
  authorUrl,
  publisherName = siteMetadata.siteName,
  publisherLogo = siteMetadata.organizationLogo,
}) => {
  const { currentLanguage } = useLanguage()
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    inLanguage: currentLanguage,
    image: imageUrl.startsWith('http') ? imageUrl : `${siteMetadata.siteUrl}${imageUrl}`,
    url,
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      '@type': 'Person',
      name: authorName,
      ...(authorUrl && { url: authorUrl }),
    },
    publisher: {
      '@type': 'Organization',
      name: publisherName,
      logo: {
        '@type': 'ImageObject',
        url: publisherLogo.startsWith('http') ? publisherLogo : `${siteMetadata.siteUrl}${publisherLogo}`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
  }
  
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

// Generic JSON-LD schema component
interface JSONLDProps {
  data: Record<string, any>
}

export const JSONLD: React.FC<JSONLDProps> = ({ data }) => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify(data)}</script>
  </Helmet>
) 